import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import PMMetricsCards from './components/PMMetricsCards';
import AssignedProjectsTable from './components/AssignedProjectsTable';
import UpcomingDeliverables from './components/UpcomingDeliverables';
import ResourceAllocationStatus from './components/ResourceAllocationStatus';
import BudgetVarianceAnalysis from './components/BudgetVarianceAnalysis';
import { projectService } from '../../services/projectService';
import { deliverableService } from '../../services/deliverableService';
import { resourceService } from '../../services/resourceService';
import { parseLocalDate } from '../../utils/dateUtils';

const PMDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, user } = useAuth();

  const [projects, setProjects] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load all projects and filter to those assigned to this PM
      const { data: allProjects } = await projectService?.getAll();
      const myProjects = (allProjects ?? [])?.filter(
        p => p?.assignedPmId === user?.id || p?.assignedPm?.id === user?.id
      );
      setProjects(myProjects);

      // Load deliverables for all assigned projects
      const deliverablePromises = myProjects?.map(p =>
        deliverableService?.getByProject(p?.id)?.then(res => (res?.data ?? [])?.map(d => ({ ...d, projectName: p?.name })))?.catch(() => [])
      );
      const deliverableResults = await Promise.all(deliverablePromises);
      const allDeliverables = deliverableResults?.flat();

      // Filter to upcoming (within 14 days) and overdue
      const today = new Date();
      const in14Days = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
      const upcoming = allDeliverables?.filter(d => {
        const due = parseLocalDate(d?.dueDate ?? d?.endDate);
        return due <= in14Days;
      });
      setDeliverables(upcoming);

      // Load resources and compute utilization per project
      const { data: allResources } = await resourceService?.getAll();
      const projectIds = new Set(myProjects.map(p => p?.id));

      // Build resource utilization from deliverable assignments
      const resourceMap = {};
      allDeliverables?.forEach(d => {
        (d?.assignedResources ?? [])?.forEach(r => {
          if (!r?.id) return;
          if (!resourceMap?.[r?.id]) {
            resourceMap[r.id] = { ...r, projectIds: new Set(), assignmentCount: 0 };
          }
          resourceMap[r.id].assignmentCount += 1;
          if (d?.projectId) resourceMap?.[r?.id]?.projectIds?.add(d?.projectId);
        });
      });

      const resourcesWithUtil = Object.values(resourceMap)?.map(r => ({
        ...r,
        projectCount: r?.projectIds?.size ?? 0,
        utilizationPercent: Math.min(Math.round((r?.assignmentCount / Math.max(myProjects?.length, 1)) * 100), 150),
      }));

      setResources(resourcesWithUtil);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('PM Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id, loadData]);

  // Compute metrics
  const metrics = React.useMemo(() => {
    const projectCount = projects?.length ?? 0;
    const totalValue = projects?.reduce((sum, p) => sum + parseFloat(p?.budget ?? p?.contractValue ?? 0), 0);

    const completedOnTime = projects?.filter(p => {
      if (p?.status !== 'Completed') return false;
      const end = parseLocalDate(p?.endDate);
      const actual = parseLocalDate(p?.actualEndDate ?? p?.updatedAt);
      return actual <= end;
    })?.length ?? 0;

    const completedProjects = projects?.filter(p => p?.status === 'Completed')?.length ?? 0;
    const onTimePercent = completedProjects > 0
      ? Math.round((completedOnTime / completedProjects) * 100)
      : projectCount > 0 ? 85 : 0;

    const totalBudget = projects?.reduce((sum, p) => sum + parseFloat(p?.budget ?? p?.contractValue ?? 0), 0);
    const totalActual = projects?.reduce((sum, p) => sum + parseFloat(p?.actualCost ?? 0), 0);
    const budgetVariance = totalBudget > 0
      ? parseFloat(((totalActual - totalBudget) / totalBudget * 100)?.toFixed(1))
      : 0;

    return { projectCount, totalValue, onTimePercent, budgetVariance };
  }, [projects]);

  const pmName = userProfile?.full_name ?? userProfile?.fullName ?? 'Project Manager';

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="LayoutDashboard" size={22} className="text-blue-600" />
                PM Dashboard
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Welcome back, <span className="font-medium text-gray-700">{pmName}</span> — your project overview
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Icon name="RefreshCw" size={12} />
                Updated {lastRefreshed?.toLocaleTimeString()}
              </span>
              <button
                onClick={loadData}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={() => navigate('/project-management')}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon name="Briefcase" size={14} />
                All Projects
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Metrics Cards */}
          <PMMetricsCards metrics={metrics} loading={loading} />

          {/* Assigned Projects Table */}
          <AssignedProjectsTable projects={projects} loading={loading} />

          {/* Bottom Grid: Deliverables + Resources + Budget */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upcoming Deliverables - spans 1 col */}
            <div className="lg:col-span-1">
              <UpcomingDeliverables deliverables={deliverables} loading={loading} />
            </div>

            {/* Resource Allocation - spans 1 col */}
            <div className="lg:col-span-1">
              <ResourceAllocationStatus resources={resources} loading={loading} />
            </div>

            {/* Budget Variance - spans 1 col */}
            <div className="lg:col-span-1">
              <BudgetVarianceAnalysis projects={projects} loading={loading} />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Icon name="Zap" size={16} className="text-yellow-500" />
              Quick Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/project-management')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="Briefcase" size={14} />
                View All Projects
              </button>
              <button
                onClick={() => navigate('/project-phases')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon name="GitBranch" size={14} />
                Project Phases
              </button>
              <button
                onClick={() => navigate('/resource-management')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon name="Users" size={14} />
                Resource Management
              </button>
              <button
                onClick={() => navigate('/analytics')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon name="BarChart3" size={14} />
                Analytics & Reports
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PMDashboard;
